/**
 * 사이드바 데이터 생성 유틸리티
 * 원본: MenuNavigationService.buildSidebarForHrefDb와 동일한 로직
 *
 * 현재 href를 기준으로 사이드바 메뉴 데이터를 생성합니다.
 */

/**
 * href에서 pageType 추출
 * 예: /page/dashboard -> dashboard
 * 예: /db/page/grid1 -> grid1
 */
const extractPageTypeFromHref = (href) => {
  if (!href) return null;
  if (href.startsWith('/page/')) {
    const pageType = href.substring(6);
    return pageType.length === 0 ? null : pageType;
  }
  if (href.startsWith('/db/page/')) {
    const pageType = href.substring(9);
    return pageType.length === 0 ? null : pageType;
  }
  return null;
};

/**
 * DFS로 href 찾기
 */
const dfsFind = (nodes, href, path) => {
  if (!nodes) return false;
  for (const node of nodes) {
    path.push(node);
    const nodeHref = node.href;
    if (href && href === nodeHref) return true;
    const children = node.children;
    if (children && dfsFind(children, href, path)) return true;
    path.pop();
  }
  return false;
};

/**
 * pageType 기반으로 메뉴 찾기
 */
const dfsFindByPageType = (nodes, pageType, path) => {
  if (!nodes || !pageType) return false;

  for (const topNode of nodes) {
    const topLabel = topNode.label;
    if (!topLabel) continue;

    const topSlug = slugify(topLabel);
    const children = topNode.children;

    if (children && children.length > 0) {
      for (const child of children) {
        const childLabel = child.label;
        if (!childLabel) continue;

        const href = child.href;
        if (!href || href === '#' || href === '') {
          const childSlug = slugify(childLabel);
          const expectedSlug = `${topSlug}-${childSlug}`;
          if (pageType === expectedSlug) {
            path.push(topNode);
            return true;
          }
        }

        if (href && href !== '#' && href !== '') {
          const hrefStr = String(href);
          if (hrefStr.startsWith('/page/') || hrefStr.startsWith('/db/page/')) {
            const hrefPageType = extractPageTypeFromHref(hrefStr);
            if (pageType === hrefPageType) {
              path.push(topNode);
              return true;
            }
          }
        }
      }
    }
  }
  return false;
};

/**
 * label을 slug 형식으로 변환
 */
const slugify = (label) => {
  if (!label) return '';
  return label.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * 현재 href 기준으로 사이드바 모델 생성
 * 원본: MenuNavigationService.buildSidebarForHrefDb
 *
 * @param {string} href - 현재 페이지의 href
 * @param {Array} menuData - 상단 메뉴 데이터 (topMenu.menu)
 * @returns {Object} 사이드바 모델 { title, menu, activeHref, enabled }
 */
export const buildSidebarForHref = (href, menuData) => {
  if (!menuData || !Array.isArray(menuData)) {
    return {
      title: '메뉴',
      menu: [],
      activeHref: href,
      enabled: false
    };
  }

  const path = [];
  let topNode = null;
  let currentMenuItem = null;

  // 1단계: 정확한 href로 찾기 시도
  if (dfsFind(menuData, href, path) && path.length > 0) {
    topNode = path[0];
    currentMenuItem = path[path.length - 1];
  } else {
    // 2단계: URL에서 pageType 추출 후 메뉴 구조에서 찾기 시도
    const pageType = extractPageTypeFromHref(href);
    if (pageType) {
      path.length = 0;
      if (dfsFindByPageType(menuData, pageType, path) && path.length > 0) {
        topNode = path[0];
        currentMenuItem = path[path.length - 1];
      }
    }
  }

  // 3단계: 여전히 찾지 못한 경우 null 반환 (메뉴에 등록되지 않은 페이지)
  if (!topNode || !currentMenuItem) {
    return null;
  }

  const model = {
    title: topNode.label || '메뉴',
    menu: topNode.children || [],
    activeHref: href,
    enabled: true,
    position: 'left' // 기본값
  };

  // submenu 속성 체크
  if (currentMenuItem) {
    const submenuAttr = currentMenuItem.submenu;
    if (submenuAttr === false) {
      model.enabled = false;
    }
  }

  // sidebar 속성 체크
  const sidebarAttr = topNode.sidebar;
  if (sidebarAttr === false) {
    model.enabled = false;
  } else if (sidebarAttr && typeof sidebarAttr === 'object') {
    // sidebar 객체에 position 속성이 있으면 사용
    if (sidebarAttr.position === 'right' || sidebarAttr.position === 'left') {
      model.position = sidebarAttr.position;
    }
  }

  // topNode에 직접 position 속성이 있으면 사용 (sidebar 객체보다 우선)
  if (topNode.position === 'right' || topNode.position === 'left') {
    model.position = topNode.position;
  }

  return model;
};

